import React, {
  useEffect,
  useState,
} from "react";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import Login from "./components/Login";
import AddBrand from "./components/AddBrand";

import CreateCampaign from "./components/CreateCampaign";
import Reports from "./components/Reports";
import CommandCenter from "./components/CommandCenter";
import JobsManagement from "./components/JobsManagement";
import SnapperInsight from "./components/SnapperInsight";
import SnappersMapView from "./components/SnappersMapView";
import SnapperList from "./components/SnapperList";
import JobsMapView from "./components/JobsMapView";
import JobDetail from "./components/JobDetail";
import JobDetailTest from "./components/JobDetail_test";
import JobList from "./components/JobList";
import UserLoginActivity from "./components/UserLoginActivity";
import Appearance from "./pages/Appearance";
import WalletWithdrawalList from "./components/WalletWithdrawalList";
import ForgotPassword from  "./components/ForgotPassword";
import AadhaarVerificationManager from "./components/AadhaarVerificationManager";

/* =========================================================
   ADMIN COMPONENTS
   ========================================================= */

import Sidebar from "./admin/Sidebar";
import AdminUserView from "./admin/AdminUserView";
import CampaignList from "./admin/CampaignList";
import BrandList from "./admin/BrandList";
import Demo_dashboard from "./components/demo_dashboard";
import Demojob_map from "./components/demojob_map";

/* =========================================================
   API BASE URL
   ========================================================= */

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";


/* =========================================================
   PROTECTED ROUTE
   ========================================================= */

function ProtectedRoute({ children }) {
  const user = localStorage.getItem("user");
  const authProvider = localStorage.getItem("authProvider");

  if (!user || !authProvider) {
    return <Navigate to="/login" replace />;
  }

  /* TechZeus login */
  if (authProvider === "techzeus") {
    const jwt = localStorage.getItem("jwt");

    if (!jwt) {
      localStorage.removeItem("user");
      localStorage.removeItem("authProvider");

      return <Navigate to="/login" replace />;
    }
  }

  /* Local DB login */
  if (authProvider === "local") {
    const localUser = localStorage.getItem("localUser");

    if (!localUser) {
      localStorage.removeItem("user");
      localStorage.removeItem("authProvider");

      return <Navigate to="/login" replace />;
    }
  }

  return children;
}


/* =========================================================
   INTERNAL ADMIN ROUTE
   ========================================================= */

function InternalAdminRoute({ children }) {
  const storedUser =
    localStorage.getItem("user") ||
    localStorage.getItem("localUser");

  const user = storedUser ? JSON.parse(storedUser) : null;

  const orgRole = user?.org_role?.toUpperCase() || "";
  const organizationType =
    user?.organization_type?.toUpperCase() || "";

  const isInternalAdmin =
    organizationType === "INTERNAL" &&
    orgRole === "ADMIN";

  if (!isInternalAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
/* =========================================================
   INTERNAL ROUTE
   ========================================================= */

function InternalRoute({ children }) {
  const storedUser =
    localStorage.getItem("user") ||
    localStorage.getItem("localUser");

  let user = null;

  try {
    user = storedUser
      ? JSON.parse(storedUser)
      : null;
  } catch (error) {
    console.error(
      "Failed to parse user information:",
      error
    );
  }

  const organizationType =
    user?.organization_type?.toUpperCase() || "";

  if (organizationType !== "INTERNAL") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/* =========================================================
   APPEARANCE ADMIN ROUTE
   ========================================================= */

function AppearanceAdminRoute({ children }) {
  const storedUser =
    localStorage.getItem("user") ||
    localStorage.getItem("localUser");

  let user = null;

  try {
    user = storedUser
      ? JSON.parse(storedUser)
      : null;
  } catch (error) {
    console.error(
      "Failed to parse user information:",
      error
    );
  }

  const orgRole =
    user?.org_role?.toUpperCase() || "";

  const organizationType =
    user?.organization_type?.toUpperCase() || "";

  const isAppearanceAdmin =
    (organizationType === "INTERNAL" &&
      orgRole === "ADMIN") ||
    (organizationType === "CUSTOMER" &&
      orgRole === "ADMIN");

  if (!isAppearanceAdmin) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return children;
}


/* =========================================================
   JOB MAP ROUTE
   ========================================================= */

function JobMapRoute({ children }) {
  const storedUser =
    localStorage.getItem("user") ||
    localStorage.getItem("localUser");

  const user = storedUser ? JSON.parse(storedUser) : null;

  const orgRole = user?.org_role?.toUpperCase() || "";
  const organizationType =
    user?.organization_type?.toUpperCase() || "";

  const isJobMapUser =
    (organizationType === "INTERNAL" && orgRole === "ADMIN") ||
    (organizationType === "CUSTOMER" && orgRole === "ADMIN") ||
    (organizationType === "CUSTOMER" && orgRole === "BM");

  if (!isJobMapUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}


/* =========================================================
   APPLICATION LAYOUT
   ========================================================= */

function ApplicationLayout({ onLogout }) {
  return (
    <div className="app-layout">

      {/* Sidebar */}
      <Sidebar onLogout={onLogout} />

      {/* Global Header */}
      <Header />

      {/* Page */}
      <main className="app-main-content">
        <Outlet />
      </main>

    </div>
  );
}


/* =========================================================
   CREATE CAMPAIGN
   ========================================================= */

function CreateCampaignWithNavigation() {
  const navigate = useNavigate();

  return (
    <CreateCampaign
      onSuccess={() => navigate("/campaign-list")}
    />
  );
}


/* =========================================================
   CAMPAIGN LIST
   ========================================================= */

function CampaignListWithNavigation() {
  const navigate = useNavigate();

  return (
    <CampaignList
      onCreateClick={() => navigate("/create-campaign")}
    />
  );
}


/* =========================================================
   PAGE TITLE
   ========================================================= */

function PageTitle() {
  const location = useLocation();

  useEffect(() => {
    const pageTitles = {
      "/login": "Login | Snap4Me",
      "/reports": "Reports | Snap4Me",
      "/dashboard": "Command Center | Snap4Me",
      "/appearance": "Appearance | Snap4Me",
      "/snapper-insight": "Snapper Intelligence | Snap4Me",
      "/snapper-insight/map": "Snapper Coverage Map | Snap4Me",
      "/snapper-list": "Snapper List | Snap4Me",
      "/jobs-map": "Job Coverage Map | Snap4Me",
      "/job-list": "Job List | Snap4Me",

      "/users": "Users | Snap4Me",
      "/wallet-withdrawals": "Wallet Withdrawals | Snap4Me",
      "/aadhaar-verification": "Aadhaar Verification | Snap4Me",
      "/brand-list": "Brand List | Snap4Me",
      "/add-brand": "Add Brand | Snap4Me",
      "/create-campaign": "Create Campaign | Snap4Me",
      "/campaign-list": "Campaign List | Snap4Me",
      "/jobs-management": "Jobs Management | Snap4Me",
    };

    if (location.pathname.startsWith("/job-detail/")) {
      document.title = "Job Detail | Snap4Me";
    } else if (location.pathname.startsWith("/job-detail-test/")) {
      document.title = "Job Detail Test | Snap4Me";
    } else {
      document.title = pageTitles[location.pathname] || "Snap4Me";
    }

  }, [location.pathname]);

  return null;
}


/* =========================================================
   APP
   ========================================================= */

function App() {

  /* =======================================================
     LOGIN STATE
     ======================================================= */

  const [loggedIn, setLoggedIn] = useState(() => {

    const user = localStorage.getItem("user");
    const authProvider =
      localStorage.getItem("authProvider");

    if (!user || !authProvider) {
      return false;
    }

    /* TechZeus */
    if (authProvider === "techzeus") {
      return !!localStorage.getItem("jwt");
    }

    /* Local */
    if (authProvider === "local") {
      return !!localStorage.getItem("localUser");
    }

    return false;
  });


  /* =======================================================
     LOGIN SUCCESS
     ======================================================= */

  const handleLoginSuccess = (
    userData,
    authProvider
  ) => {

    const provider =
      authProvider || "local";

    /* Common user */
    localStorage.setItem(
      "user",
      JSON.stringify(userData)
    );

    /* Auth provider */
    localStorage.setItem(
      "authProvider",
      provider
    );

    /* TechZeus */
    if (provider === "techzeus") {
      localStorage.removeItem("localUser");
    }

    /* Local Database */
    if (provider === "local") {
      localStorage.removeItem("jwt");

      localStorage.setItem(
        "localUser",
        JSON.stringify(userData)
      );
    }

    setLoggedIn(true);

    window.dispatchEvent(
      new Event("appearance:auth-changed")
    );
  };


  /* =======================================================
     LOGOUT
     ======================================================= */

  const handleLogout = async () => {
    const jwt = localStorage.getItem("jwt");
    const userRaw = localStorage.getItem("user");

    let userData = null;

    try {
      userData = userRaw ? JSON.parse(userRaw) : null;
    } catch (error) {
      console.error("LOGOUT: Could not parse stored user:", error);
    }

    // ---------------------------------------------------------
    // Record logout activity BEFORE clearing authentication
    // ---------------------------------------------------------
    try {
      if (jwt && userData?.id) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
            "X-User-ID": String(userData.id),
          },
        });

        console.log("LOGOUT AUDIT: Logout activity recorded");
      } else {
        console.warn(
          "LOGOUT AUDIT: Missing JWT or user ID. Skipping audit log."
        );
      }
    } catch (error) {
      // IMPORTANT:
      // Logout must still happen even if audit logging fails.
      console.error("LOGOUT AUDIT ERROR:", error);
    }

    // ---------------------------------------------------------
    // Existing logout logic
    // ---------------------------------------------------------
    localStorage.removeItem("user");
    localStorage.removeItem("localUser");
    localStorage.removeItem("jwt");
    localStorage.removeItem("token");
    localStorage.removeItem("authProvider");

    setLoggedIn(false);

    window.dispatchEvent(
      new Event("appearance:auth-changed")
    );
  };


  /* =======================================================
     ROUTER
     ======================================================= */

  return (
    <BrowserRouter>

      <PageTitle />

      <Routes>
         
        {/* LOGIN */}
        <Route
          path="/login"
          element={
            loggedIn ? (
              <Navigate
                to="/reports"
                replace
              />
            ) : (
              <Login
                onApiLoginSuccess={handleLoginSuccess}
                onLocalLoginSuccess={handleLoginSuccess}
              />
            )
          }
        />
        {/* FORGOT PASSWORD */}
        <Route
          path="/forgot-password"
          element={<ForgotPassword />}
        />
        

        {/* PUBLIC KAMDHENU DASHBOARDS */}
        <Route path="/demo_dashboard" element={<Demo_dashboard />} />
        <Route path="/demojob_map" element={<Demojob_map />} />

        {/* PROTECTED APPLICATION */}
        <Route
          element={
            <ProtectedRoute>
              <ApplicationLayout
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          }
        >

          <Route
            path="/reports"
            element={
              <Reports
                onLogout={handleLogout}
              />
            }
          />

          <Route
            path="/dashboard"
            element={<CommandCenter />}
          />

          <Route
            path="/appearance"
            element={
              <AppearanceAdminRoute>
                <Appearance />
              </AppearanceAdminRoute>
            }
          />

          <Route
            path="/snapper-insight"
            element={
              <InternalAdminRoute>
                <SnapperInsight />
              </InternalAdminRoute>
            }
          />

          <Route
            path="/snapper-insight/map"
            element={
              <InternalAdminRoute>
                <SnappersMapView />
              </InternalAdminRoute>
            }
          />

          <Route
            path="/jobs-map"
            element={
              <JobMapRoute>
                <JobsMapView />
              </JobMapRoute>
            }
          />

          <Route
            path="/job-list"
            element={
              <JobMapRoute>
                <JobList />
              </JobMapRoute>
            }
          />
          <Route
            path="/aadhaar-verification"
            element={
              <InternalAdminRoute>
                <AadhaarVerificationManager />
              </InternalAdminRoute>
            }
          />

          <Route
          path="/wallet-withdrawals"
          element={
            <InternalRoute>
              <WalletWithdrawalList />
            </InternalRoute>
          }
        />
          <Route
            path="/user-login-activity"
            element={
                <ProtectedRoute>
                    <InternalAdminRoute>
                        <UserLoginActivity />
                    </InternalAdminRoute>
                </ProtectedRoute>
            }
        />

          <Route
            path="/job-detail/:jobId"
            element={
              <JobMapRoute>
                <JobDetail />
              </JobMapRoute>
            }
          />

          <Route
            path="/job-detail-test/:jobId"
            element={
              <JobMapRoute>
                <JobDetailTest />
              </JobMapRoute>
            }
          />

          <Route
            path="/snapper-list"
            element={
              <InternalAdminRoute>
                <SnapperList />
              </InternalAdminRoute>
            }
          />

          <Route
            path="/users"
            element={<AdminUserView />}
          />

          <Route
            path="/brand-list"
            element={<BrandList />}
          />

          <Route
            path="/add-brand"
            element={<AddBrand />}
          />

          <Route
            path="/campaign-list"
            element={
              <CampaignListWithNavigation />
            }
          />

          <Route
            path="/jobs-management"
            element={
              <JobsManagement
                onLogout={handleLogout}
              />
            }
          />

          <Route
            path="/create-campaign"
            element={
              <CreateCampaignWithNavigation />
            }
          />

        </Route>

        {/* DEFAULT */}
        <Route
          path="/"
          element={
            loggedIn ? (
              <Navigate
                to="/reports"
                replace
              />
            ) : (
              <Navigate
                to="/login"
                replace
              />
            )
          }
        />

        {/* UNKNOWN */}
        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />

      </Routes>

    </BrowserRouter>
  );
}

export default App;